import { ArrowUpRight } from "lucide-react";
import { NUM } from "../lib/dashboardTokens";

const THEMES = {
  blue: {
    border: "border-blue-100 hover:border-blue-200",
    iconBg: "bg-blue-50 text-blue-600",
    accentGlow: "from-blue-500/10 to-transparent",
  },
  amber: {
    border: "border-amber-100 hover:border-amber-200",
    iconBg: "bg-amber-50 text-amber-600",
    accentGlow: "from-amber-500/10 to-transparent",
  },
  rose: {
    border: "border-rose-100 hover:border-rose-200",
    iconBg: "bg-rose-50 text-rose-600",
    accentGlow: "from-rose-500/10 to-transparent",
  },
};

// A clickable summary card — the arrow in the footer signals "this goes somewhere" the
// same way it did on the old Live Status page. Used on Overview for Customer Approval
// Pending / Pending Response / Escalations, each linking through to its own page.
export function MetricCard({ title, count, icon: Icon, theme = "blue", subtext, isLoading, testId, onClick }) {
  const styles = THEMES[theme] || THEMES.blue;
  const interactive = typeof onClick === "function";

  return (
    <div
      data-testid={testId}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={`relative overflow-hidden rounded-xl border bg-white p-5 sm:p-6 shadow-sm transition-all duration-200 ${styles.border} ${
        interactive ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" : ""
      }`}
    >
      <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${styles.accentGlow} blur-2xl`} />

      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-medium shadow-xs ${styles.iconBg}`}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <h2 className="font-work text-sm font-semibold text-slate-600 uppercase tracking-wide">{title}</h2>
      </div>

      <div className="mt-5 flex items-baseline justify-between">
        {isLoading ? (
          <div className="h-10 w-20 animate-pulse rounded-md bg-slate-100" />
        ) : (
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-bold tracking-tight text-slate-900 ${NUM}`}>{count}</span>
            <span className="text-xs font-medium text-slate-400">active</span>
          </div>
        )}
      </div>

      {subtext && (
        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
          <span className="truncate font-medium">{subtext}</span>
          <ArrowUpRight size={14} className="text-slate-300 shrink-0" />
        </div>
      )}
    </div>
  );
}
